<template>
  <div class="financials-widget">
    <header
      v-if="data.userId || canEdit"
      class="view-header"
    >
      <CalicoChip text="Financials" />
      <label class="body--xsmall">As of {{ date }}</label>
    </header>

    <div
      v-if="!data.userId && canEdit"
      class="inactive-widget"
    >
      <CalicoIconCard class="icon-card">
        <i class="bi bi-bar-chart-fill contained-icon-medium" />
        <h1 class="heading--medium">
          Financial Health
        </h1>
        <p class="body--medium">
          See how lenders view your business’ creditworthiness.
          Add information from your most recent income statement, business balance sheet, and personal balance sheet.
        </p>
        <div style="width: fit-content;">
          <button
            class="button-primary button-primary--small"
            @click="$router.push('/business/financials')"
          >
            <i class="bi bi-plus-lg" /> Add Financials
          </button>
        </div>
      </CalicoIconCard>
    </div>

    <div
      v-else-if="data.userId"
      class="active-widget"
    >
      <div class="credit-risk-rating">
        <span class="label--medium">Credit Risk Rating</span>
        <CalicoCreditRiskRating :credit-risk-rating="data.creditGradeRiskClass" />
      </div>

      <div class="credit-box-container">
        <CalicoCreditBox>
          <CalicoCreditBoxItem
            label="Debt Coverage"
            :score="normalizeScore(data.debtCoverage.score, 'expanded')"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_DEBT_COVERAGE_HEADER, data.debtCoverage.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_DEBT_COVERAGE_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_DEBT_COVERAGE_BODY"
          />
          <CalicoCreditBoxItem
            label="Profitability"
            :score="normalizeScore(data.profitability.score)"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_PROFITABILITY_HEADER, data.profitability.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_PROFITABILITY_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_PROFITABILITY_BODY"
          />
          <CalicoCreditBoxItem
            label="Liquidity"
            :score="normalizeScore(data.liquidity.score)"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_LIQUIDITY_HEADER, data.liquidity.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_LIQUIDITY_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_LIQUIDITY_BODY"
          />
          <CalicoCreditBoxItem
            label="Leverage"
            :score="normalizeScore(data.leverage.score)"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_LEVERAGE_HEADER, data.leverage.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_LEVERAGE_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_LEVERAGE_BODY"
          />
          <CalicoCreditBoxItem
            label="Capital Adequacy"
            :score="normalizeScore(data.capitalAdequacy.score)"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_CAPITAL_ADEQUACY_HEADER, data.capitalAdequacy.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_CAPITAL_ADEQUACY_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_CAPITAL_ADEQUACY_BODY"
          />
          <CalicoCreditBoxItem
            label="Collateral Coverage"
            :score="normalizeScore(data.collateralCoverage.score, 'expanded')"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_COLLATERAL_COVERAGE_HEADER, data.collateralCoverage.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_COLLATERAL_COVERAGE_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_COLLATERAL_COVERAGE_BODY"
          />
          <CalicoCreditBoxItem
            label="Guarantor Strength"
            :score="normalizeScore(data.guarantorStrength.score)"
            :value="formatFinancialValueByKPI(Tooltips.CREDITBOX_GUARANTOR_STRENGTH_HEADER, data.guarantorStrength.rawValue)"
            :tooltip-header="Tooltips.CREDITBOX_GUARANTOR_STRENGTH_HEADER"
            :tooltip-body="Tooltips.CREDITBOX_GUARANTOR_STRENGTH_BODY"
          />
        </CalicoCreditBox>
      </div>

      <div
        v-if="!$route.params.id"
        style="width: fit-content;"
      >
        <button
          class="button-secondary"
          @click="$router.push('/business/financials')"
        >
          <span class="label--medium">
            <i class="bi bi-pencil-fill" /> Update Financials
          </span>
        </button>
      </div>

      <div class="financials-table">
        <div class="data-group">
          <div class="label--large">
            Income Statement
          </div>
          <div class="info-item">
            <label class="label--medium">Revenue</label>
            <div class="body--medium">
              {{ formatCurrency(data.revenue) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Cost of Goods Sold</label>
            <div class="body--medium">
              {{ formatCurrency(data.costOfGoodsSold) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Operating Income</label>
            <div class="body--medium">
              {{ formatCurrency(data.operatingIncome) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Net Income</label>
            <div class="body--medium">
              {{ formatCurrency(data.netIncome) }}
            </div>
          </div>
        </div>

        <div class="data-group">
          <div class="label--large">
            Balance Sheet
          </div>
          <div class="info-item">
            <label class="label--medium">Current Assets</label>
            <div class="body--medium">
              {{ formatCurrency(data.currentAssets) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Total Assets</label>
            <div class="body--medium">
              {{ formatCurrency(data.totalAssets) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Other Assets</label>
            <div class="body--medium">
              {{ formatCurrency(data.otherAssets) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Current Liabilities</label>
            <div class="body--medium">
              {{ formatCurrency(data.currentLiabilities) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Total Liabilities</label>
            <div class="body--medium">
              {{ formatCurrency(data.totalLiabilities) }}
            </div>
          </div>
        </div>

        <div class="data-group">
          <div class="label--large">
            Personal Balance Sheet
          </div>
          <div class="info-item">
            <label class="label--medium">Total Personal Assets</label>
            <div class="body--medium">
              {{ formatCurrency(data.totalPersonalAssets) }}
            </div>
          </div>
          <div class="info-item">
            <label class="label--medium">Total Personal Liabilities</label>
            <div class="body--medium">
              {{ formatCurrency(data.totalPersonalLiabilities) }}
            </div>
          </div>
        </div>

        <div class="data-group">
          <div class="label--large">
            Loan Information
          </div>
          <div class="info-item">
            <label class="label--medium">Loan Amount</label>
            <div class="body--medium">
              {{ formatCurrency(data.loanAmount) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CalicoChip from '@/components/calico/chip/index.vue';
import CalicoIconCard from '@/components/calico/iconCard/index.vue';
import CalicoCreditRiskRating from '@/components/calico/creditRiskRating/index.vue';
import CalicoCreditBox from '@/components/pattern/creditBox/index.vue';
import CalicoCreditBoxItem from '@/components/calico/creditBoxItem/index.vue';
import { formatCurrency, normalizeScore } from '@/utils/helper';
import { formatFinancialValueByKPI } from '@/utils/financialFormatter';
import { Tooltips } from '@/enums';

export default {
  name: 'FinancialsWidget',
  components: {
    CalicoChip,
    CalicoIconCard,
    CalicoCreditRiskRating,
    CalicoCreditBox,
    CalicoCreditBoxItem
  },
  props: {
    data: { type: Object, required: true },
    canEdit: {
      type: Boolean,
      default: false
    },
    date: {
      type: String,
      default: () => new Date().toISOString().split('T')[0]
    }
  },
  data() {
    return {
      showBanner: true,
      Tooltips,
    };
  },
  methods: {    
    formatCurrency,
    formatFinancialValueByKPI,
    normalizeScore
  },
};
</script>

<style scoped lang="scss">
.financials-widget {
  @include stack-space($space-large);
  padding: 0 $space-medium $space-medium $space-medium;

  .view-header {
    @include inline-space($space-medium);
    align-items: center;

    .body--xsmall {
      color: $color-neutral;
    }
  }

  .inactive-widget,
  .active-widget {
    @include stack-space($space-large);

    .credit-risk-rating {
      @include stack-space($space-small);
    }

    .credit-box-container {
      background-color: $color-background-primary;
      border-radius: $radius-large;
    }
  }

  .inactive-widget {
    .contained-icon-medium {
      background-color: $color-cyan;
      border: none;
    }
  }

  .financials-table {
    @include stack-space($space-large);

    .data-group {
      @include stack-space($space-medium);
      border-left: 2px solid $color-black;
      padding-left: $space-large;
      gap: $space-medium;
    }

    .info-item {
      @include stack-space($space-xsmall);
    }
  }
}
</style>